import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Tag, Row, Col, Tooltip as AntTooltip, Select } from "antd";
import {
  LineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Line,
  ResponsiveContainer,
} from "recharts";
import scalesConfig from "../../../../helpers/physioscaleskeys";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import scaleIcon from "../../../../assets/images/OPImages/noDataScales.svg";
import { ReloadOutlined } from "@ant-design/icons";

const toTitleCase = (text = "") =>
  text
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const formatISTDateTime = (ts, { isMobile } = { isMobile: false }) => {
  if (!ts) return { line1: "—", line2: "" };
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return { line1: "—", line2: "" };

  const line1 = d.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const line2 = d.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return { line1, line2: line2 || (isMobile ? "" : "") };
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

const getPointerClient = (e) => {
  const src = e?.nativeEvent || e;
  const t = src?.touches?.[0] || src?.changedTouches?.[0];
  const x = t?.clientX ?? src?.clientX;
  const y = t?.clientY ?? src?.clientY;
  if (typeof x !== "number" || typeof y !== "number") return null;
  if (Number.isNaN(x) || Number.isNaN(y)) return null;
  return { x, y };
};

const ProgressChartOp = ({
  scaleKey,
  tags,
  chartData,
  subItemKey,
  height,
  useMobileMetricDropdown = false,
}) => {
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);
  const scrollChartAnchorRef = useRef(null);
  const chartShellRef = useRef(null);
  const lastPointerRef = useRef(null);

  useEffect(() => {
    setSelectedKeys([]);
  }, [subItemKey]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobileViewport(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);


  useEffect(() => {
    const hideOnScroll = (e) => {
      if (e?.target?.closest?.(".op-progress-fixed-tooltip")) return;
      setTooltipSuppressed(true);
    };
    const scroller =
      chartShellRef.current?.closest(
        ".ip-scale-drawer-content, .op-scale-drawer-content, .ant-drawer-body"
      ) || null;

    scroller?.addEventListener("scroll", hideOnScroll, { passive: true });
    window.addEventListener("scroll", hideOnScroll, {
      passive: true,
      capture: true,
    });
    return () => {
      scroller?.removeEventListener("scroll", hideOnScroll);
      window.removeEventListener("scroll", hideOnScroll, { capture: true });
    };
  }, []);

  const handleTagClick = (key) => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
    );
  };

  if (!chartData || chartData.length === 0) {
    return (
      <NoDataPage title="No data recorded for this scale" image={scaleIcon} />
    );
  }

  const metricOptions = tags.map((tagKey) => {
    const tagConfig = subItemKey
      ? scalesConfig[scaleKey]?.subItems?.[subItemKey]?.keys?.[tagKey]
      : scalesConfig[scaleKey]?.keys?.[tagKey] || {};
    return {
      value: tagKey,
      label: tagConfig?.name || tagKey.replace(/_/g, " "),
    };
  });

  const showMetricDropdown = useMobileMetricDropdown || isMobileViewport;
  const useScrollableMobileChart = isMobileViewport;

  const mobileChartWidth = Math.max(320, (chartData?.length || 0) * 110);
  const mobileChartHeight = height || 330;
  const mobileChartData = useScrollableMobileChart
    ? (chartData || []).map((d, idx) => ({
        ...d,
        _tickKey: `${d?._ts ?? d?.name}|${idx}`,
      }))
    : chartData;

  const mobileXTicks = useScrollableMobileChart
    ? (mobileChartData || []).map((d) => d?._tickKey)
    : [];

  const formatTooltipLabel = (label) => {
    if (label === null || label === undefined) return "—";
    const raw = String(label);
    const tsPart = raw.includes("|") ? raw.split("|")[0] : raw;
    const ts =
      tsPart && Number.isFinite(Number(tsPart)) ? Number(tsPart) : tsPart;
    const { line1, line2 } = formatISTDateTime(ts, {
      isMobile: useScrollableMobileChart,
    });
    return line2 ? `${line1} ${line2}` : line1;
  };

  const tooltipValueFormatter = (value, name, props) => {
    const dataKey = props?.dataKey || name;

    let label = dataKey;
    if (scaleKey === "PeakFlowMeterTest") {
      if (dataKey === "test_PEFR") label = "Test PEFR";
      else if (dataKey === "predicted_PEFR") label = "Predicted PEFR";
      else label = toTitleCase(dataKey);
    } else if (scaleKey === "FingerGripTest") {
      if (dataKey === "left_hand") label = "Left Hand Strength (kg)";
      else if (dataKey === "right_hand") label = "Right Hand Strength (kg)";
      else label = toTitleCase(dataKey);
    } else if (scaleKey === "TrunkControlTest" && dataKey === "sitting_to_standing") {
      label = "Dynamic Reaching";
    } else {
      label = toTitleCase(dataKey);
    }

    return [value, label];
  };

  const MobileFixedTooltip = ({ active, payload, label, coordinate }) => {
    const tooltipRef = useRef(null);
    const [pos, setPos] = useState(null);

    const MAX_VISIBLE_ROWS = 7;
    const ROW_HEIGHT = 22; 
    const HEADER_HEIGHT = 28;
    const VERTICAL_PADDING = 24;
    const maxTooltipBodyHeight = MAX_VISIBLE_ROWS * ROW_HEIGHT;
    const maxTooltipHeight = HEADER_HEIGHT + VERTICAL_PADDING + maxTooltipBodyHeight;

    useLayoutEffect(() => {
      if (
        tooltipSuppressed ||
        !active ||
        !payload?.length ||
        !coordinate ||
        !scrollChartAnchorRef.current
      ) {
        setPos(null);
        return;
      }

      const anchorRect = scrollChartAnchorRef.current.getBoundingClientRect();

      const pointer = lastPointerRef.current;
      const approxX =
        typeof coordinate?.x === "number"
          ? anchorRect.left + coordinate.x
          : (pointer?.x ?? anchorRect.left);
      const approxY =
        typeof coordinate?.y === "number"
          ? anchorRect.top + coordinate.y
          : (pointer?.y ?? anchorRect.top);

      const el = tooltipRef.current;
      const w = el?.offsetWidth ?? 280;
      const measuredH = el?.offsetHeight ?? 120;
      const h = Math.min(measuredH, maxTooltipHeight);

      const padding = 12;
      let left = approxX + 16;
      let top = approxY - 12;
      if (left + w > window.innerWidth - padding) {
        left = approxX - w - 16;
      }
      if (top + h > window.innerHeight - padding) {
        top = approxY - h - 14;
      }
      left = clamp(left, padding, window.innerWidth - w - padding);
      top = clamp(top, padding, window.innerHeight - h - padding);

      setPos({ left, top });
    }, [active, payload, label, coordinate, maxTooltipHeight, tooltipSuppressed]);

    if (tooltipSuppressed || !active || !payload?.length) return null;

    const header = formatTooltipLabel(label);

    const node = (
      <div
        ref={tooltipRef}
        className="op-progress-fixed-tooltip"
        style={{
          position: "fixed",
          left: pos?.left ?? 0,
          top: pos?.top ?? 0,
          visibility: pos ? "visible" : "hidden",
          zIndex: 10000,
          pointerEvents: "auto",
          backgroundColor: "rgba(31, 31, 31, 0.88)",
          borderRadius: 10,
          color: "#ffffff",
          maxWidth: 300,
          maxHeight: maxTooltipHeight,
          padding: 12,
          whiteSpace: "normal",
          border: "1px solid rgba(255,255,255,0.10)",
          boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 8, fontSize: 13 }}>{header}</div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            maxHeight: maxTooltipBodyHeight,
            overflowY: "auto",
            paddingRight: 6,
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
          }}
        >
          {payload.map((p, idx) => {
            const [val, lbl] = tooltipValueFormatter(p?.value, p?.name, {
              dataKey: p?.dataKey,
              payload: p?.payload,
            });
            if (val === null || val === undefined || val === "") return null;
            return (
              <div
                key={`${p?.dataKey ?? p?.name ?? "k"}-${idx}`}
                style={{ fontSize: 12, lineHeight: "18px" }}
              >
                <span style={{ fontWeight: 600 }}>{lbl}</span>
                <span>{`: ${val}`}</span>
              </div>
            );
          })}
        </div>
      </div>
    );

    return ReactDOM.createPortal(node, document.body);
  };

  return (
    <>
      {showMetricDropdown ? (
        <Select
                        allowClear
                        placeholder="Select measurement"
                        className="op-rom-mobile-metric-select"
                        style={{
                          width: "100%",
                          maxWidth: "100%",
                          marginBottom: 12,
                          display: "block",
                        }}
                        value={selectedKeys.length === 1 ? selectedKeys[0] : undefined}
                        onChange={(v) => setSelectedKeys(v ? [v] : [])}
                        options={metricOptions}
                        optionFilterProp="label"
                      />
      ) : (
        <Row gutter={[8, 8]} justify="center" style={{ marginBottom: 16 }}>
          {tags.map((tagKey, index) => {
            const tagConfig = subItemKey
              ? scalesConfig[scaleKey]?.subItems?.[subItemKey]?.keys?.[tagKey]
              : scalesConfig[scaleKey]?.keys?.[tagKey] || {};
            const isSelected = selectedKeys.includes(tagKey);
            return (
              <Col key={index}>
                <Tag
                  style={{
                    fontSize: "12px",
                    fontWeight: "200",
                    backgroundColor: isSelected ? tagConfig?.color : "white",
                    borderColor: tagConfig?.color,
                    color: isSelected ? "white" : tagConfig?.color,
                    cursor: "pointer",
                  }}
                  onClick={() => handleTagClick(tagKey)}
                >
                  {tagConfig?.name || tagKey.replace(/_/g, " ")}
                </Tag>
              </Col>
            );
          })}
          <Col span={2}>
            <AntTooltip title="Clear All">
              <Tag
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedKeys([])}
              >
                <ReloadOutlined />
              </Tag>
            </AntTooltip>
          </Col>
        </Row>
      )}
      <Row
        gutter={useMobileMetricDropdown ? [0, 0] : [8, 8]}
        justify="start"
        style={{ marginBottom: 16 }}
      >
        <Col
          span={24}
          style={useMobileMetricDropdown ? { paddingInline: 0 } : undefined}
        >
          {/*
            On mobile, show every X-axis point by forcing tick rendering (`interval={0}`)
            and enabling horizontal scrolling via a width scaled to number of points.
          */}
          <div
            ref={chartShellRef}
            className={
              useScrollableMobileChart
                ? "op-progress-chart-wrap op-progress-chart-wrap--flush"
                : "op-progress-chart-wrap"
            }
            style={
              useScrollableMobileChart
                ? {
                    overflowX: "auto",
                    width: "100%",
                    WebkitOverflowScrolling: "touch",
                    touchAction: "pan-x",
                  }
                : {
                    height: "50vh",
                    overflow: "visible",
                    paddingBottom: "20px",
                  }
            }
            onTouchStart={(e) => {
              lastPointerRef.current = getPointerClient(e);
              setTooltipSuppressed(false);
            }}
            onMouseDown={(e) => {
              lastPointerRef.current = getPointerClient(e);
              setTooltipSuppressed(false);
            }}
            onMouseMove={(e) => {
              lastPointerRef.current = getPointerClient(e);
            }}
            onScroll={() => setTooltipSuppressed(true)}
          >
            {useScrollableMobileChart ? (
              <div ref={scrollChartAnchorRef} style={{ display: "inline-block", paddingRight: 24 }}>
                <LineChart
                  width={mobileChartWidth}
                  height={mobileChartHeight}
                  data={mobileChartData}
                  margin={{ top: 16, right: 40, bottom: 70, left: 4 }}
                  onMouseMove={(state, e) => {
                    const pointer = getPointerClient(e);
                    if (pointer) lastPointerRef.current = pointer;
                    if (tooltipSuppressed) setTooltipSuppressed(false);
                  }}
                  onClick={(state, e) => {
                    const pointer = getPointerClient(e);
                    if (pointer) lastPointerRef.current = pointer;
                    if (tooltipSuppressed) setTooltipSuppressed(false);
                  }}
                >
                  <CartesianGrid
                    stroke="#eee"
                    strokeDasharray="5 5"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="_tickKey"
                    type="category"
                    scale="point"
                    ticks={mobileXTicks}
                    interval={0}
                    minTickGap={0}
                    tickMargin={6}
                    tick={(tickProps) => {
                      const { x, y, payload } = tickProps || {};
                      const tickKey = payload?.value;
                      const tsPart = String(tickKey).split("|")[0];
                      const ts =
                        tsPart && Number.isFinite(Number(tsPart))
                          ? Number(tsPart)
                          : tsPart;
                      const { line1, line2 } = formatISTDateTime(ts, {
                        isMobile: true,
                      });

                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text textAnchor="middle" fill="#667085" fontSize={8}>
                            <tspan x="0" dy="0">
                              {line1}
                            </tspan>
                            <tspan x="0" dy="10">
                              {line2}
                            </tspan>
                          </text>
                        </g>
                      );
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={["auto", "auto"]}
                    width={36}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{
                      visibility: "hidden",
                      pointerEvents: "none",
                      zIndex: 9999,
                    }}
                    labelFormatter={formatTooltipLabel}
                    content={<MobileFixedTooltip />}
                    formatter={tooltipValueFormatter}
                  />
                  {(selectedKeys.length === 0 ? tags : selectedKeys).map(
                    (key) => {
                      const keyConfig = subItemKey
                        ? scalesConfig[scaleKey]?.subItems?.[subItemKey]
                            ?.keys?.[key]
                        : scalesConfig[scaleKey]?.keys?.[key] || {};
                      return (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={keyConfig?.color || "#8884d8"}
                          strokeWidth={1.5}
                          connectNulls
                          name={
                            scaleKey === "PeakFlowMeterTest"
                              ? key === "test_PEFR"
                                ? "Test PEFR"
                                : key === "predicted_PEFR"
                                ? "Predicted PEFR"
                                : toTitleCase(key)
                              : scaleKey === "TrunkControlTest" &&
                                key === "sitting_to_standing"
                              ? "Dynamic Reaching"
                              : toTitleCase(key)
                          }
                          isAnimationActive={false}
                        />
                      );
                    }
                  )}
                </LineChart>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 20, right: 48, bottom: 48, left: 8 }}
                >
                  <CartesianGrid
                    stroke="#eee"
                    strokeDasharray="5 5"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="_ts"
                    interval="preserveStartEnd"
                    minTickGap={40}
                    tickMargin={8}
                    tick={(tickProps) => {
                      const { x, y, payload } = tickProps || {};
                      const ts = payload?.value;
                      const { line1, line2 } = formatISTDateTime(ts, {
                        isMobile: false,
                      });

                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text
                            textAnchor="middle"
                            fill="#667085"
                            fontSize={11}
                          >
                            <tspan x="0" dy="0">
                              {line1}
                            </tspan>
                            <tspan x="0" dy="15">
                              {line2}
                            </tspan>
                          </text>
                        </g>
                      );
                    }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis domain={["auto", "auto"]} tick={{ fontSize: 12 }} />
                  <Tooltip
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ zIndex: 9999 }}
                    labelFormatter={formatTooltipLabel}
                    contentStyle={{
                      backgroundColor: "rgba(31, 31, 31, 0.78)",
                      borderRadius: "8px",
                      color: "#ffffff",
                      maxWidth: "280px",
                      overflow: "visible",
                      padding: "12px",
                      whiteSpace: "normal",
                      border: "1px solid #444",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
                    }}
                    itemStyle={{
                      color: "#ffffff",
                      fontSize: "12px",
                      lineHeight: "18px",
                    }}
                    labelStyle={{ color: "#ffffff", fontWeight: 600 }}
                    formatter={tooltipValueFormatter}
                  />
                  {(selectedKeys.length === 0 ? tags : selectedKeys).map(
                    (key) => {
                      const keyConfig = subItemKey
                        ? scalesConfig[scaleKey]?.subItems?.[subItemKey]
                            ?.keys?.[key]
                        : scalesConfig[scaleKey]?.keys?.[key] || {};
                      return (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={keyConfig?.color || "#8884d8"}
                          strokeWidth={1.5}
                          connectNulls
                          name={
                            scaleKey === "PeakFlowMeterTest"
                              ? key === "test_PEFR"
                                ? "Test PEFR"
                                : key === "predicted_PEFR"
                                ? "Predicted PEFR"
                                : toTitleCase(key)
                              : scaleKey === "TrunkControlTest" &&
                                key === "sitting_to_standing"
                              ? "Dynamic Reaching"
                              : toTitleCase(key)
                          }
                          isAnimationActive={false}
                        />
                      );
                    }
                  )}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default ProgressChartOp;
